import { defineComponent, computed, onBeforeMount, ref } from 'vue'
import { useCatalogStore } from '@/store/catalog'
import { useMainPageStore } from '@/store/mainPage'
import { useServicesStore } from '@/store/services'
import type { PropType } from 'vue'
import type { CatalogListSelectOffer } from '@/types/catalog'
import type {
  IPageFormProps,
  AddMainPageBlockProductPayload,
  UpdateMainPageBlockProductPayload,
  AddMainPageBlockServicePayload,
  UpdateMainPageBlockServicePayload,
  IProductBlockPage,
} from '@/types/mainPage'

interface ISelectItem {
  id: number
  title: string
}

export default defineComponent({
  name: 'HomePageForm',
  props: {
    props: {
      type: Object as PropType<IPageFormProps>,
      required: true,
    },
  },
  emits: ['close'],
  setup(props, { emit }) {
    const selectedItem = ref<CatalogListSelectOffer | null>(null)
    const options = ref<CatalogListSelectOffer[]>([])
    const isListLoading = ref(false)
    const isLoading = ref(false)
    const error = ref('')

    const isEditing = computed(() => !!props.props.selectedPage)
    const type = computed(() => props.props.type)

    const catalogStore = useCatalogStore()
    const mainPageStore = useMainPageStore()
    const servicesStore = useServicesStore()

    const valueUpdate = (key: string, value: ISelectItem) => (selectedItem.value = value)

    const resetErrors = () => {
      mainPageStore.error = null
      error.value = ''
    }

    const addReq = async () => {
      if (!selectedItem.value)
        return

      switch (type.value) {
        case 'products':
          const productBody: AddMainPageBlockProductPayload = {
            blockId: +props.props.blockId,
            productId: +selectedItem.value.id,
          }
          await mainPageStore.addBlockProduct(productBody)
          break

        case 'services':
          const serviceBody: AddMainPageBlockServicePayload = {
            blockId: +props.props.blockId,
            serviceId: +selectedItem.value.id,
          }
          await mainPageStore.addBlockService(serviceBody)
          break
        default:
          break
      }
    }

    const updateReq = async () => {
      if (!selectedItem.value || !props.props.selectedPage)
        return

      switch (type.value) {
        case 'products':
          const productBody: UpdateMainPageBlockProductPayload = {
            blockId: +props.props.blockId,
            pageId: props.props.selectedPage.id,
            data: {
              productId: +selectedItem.value.id,
            },
          }
          await mainPageStore.updateBlockProduct(productBody)
          break

        case 'services':
          const serviceBody: UpdateMainPageBlockServicePayload = {
            blockId: +props.props.blockId,
            pageId: props.props.selectedPage.id,
            data: {
              serviceId: +selectedItem.value.id,
            },
          }
          await mainPageStore.updateBlockService(serviceBody)
          break
        default:
          break
      }
    }

    const handleSubmit = async () => {
      resetErrors()
      isLoading.value = true
      try {
        if (isEditing.value) {
          await updateReq()
        } else {
          await addReq()
        }
        emit('close')
      } catch {
        if (mainPageStore.error) {
          error.value = mainPageStore.error.error
        }
      } finally {
        isLoading.value = false
      }
    }

    onBeforeMount(async () => {
      isListLoading.value = true
      try {
        if (props.props.type === 'products') {
          await catalogStore.getOffersListForSelect()
          options.value = catalogStore.offersListForSelect
          const selectedPageOption = options.value.find(
            o => o.id === (props.props.selectedPage as IProductBlockPage)?.productId,
          )
          if (selectedPageOption) {
            selectedItem.value = selectedPageOption
          }
        } else if (props.props.type === 'services') {
          await servicesStore.getServicesSelect()
          if (servicesStore.servicesSelect) {
            options.value = servicesStore.servicesSelect.map(service => ({
              id: service.id,
              title: service.name,
            }))

            const selectedPageOption = options.value.find(
              o => o.id === props.props.selectedPage?.serviceId,
            )
            if (selectedPageOption) {
              selectedItem.value = selectedPageOption
            }
          }
        }
      } catch (error) {
        console.error(error)
      } finally {
        isListLoading.value = false
      }
    })
    return {
      options,
      selectedItem,
      isEditing,
      isLoading,
      isListLoading,
      valueUpdate,
      handleSubmit,
    }
  },
})
